import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, Building2, Calendar, ArrowRight, CheckCircle2, Euro, Search, Zap, Shield, FolderOpen, Handshake, Sparkles, TrendingUp, Linkedin } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function Landing() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const goToSignup = () => {
    navigate('/login', { state: { isSignup: true } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <>
      {/* Navigation */}
      <nav className="bg-white/70 backdrop-blur-xl sticky top-0 z-50 border-b border-gray-100" role="navigation" aria-label="Navigation principale">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <img
                src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
                alt="Campaly - Logo CRM Marketing d'Influence"
                className="w-9 h-9 object-contain"
                width="36"
                height="36"
              />
              <span className="text-2xl font-light tracking-tight text-gray-900">Campaly</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 text-gray-700 hover:text-gray-900 transition-colors font-light text-sm"
                aria-label="Se connecter à Campaly"
              >
                Connexion
              </button>
              <button
                onClick={goToSignup}
                className="group px-6 py-2.5 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all font-light text-sm flex items-center gap-2"
                aria-label="Commencer avec Campaly gratuitement"
              >
                Commencer
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32" aria-labelledby="hero-title">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/30 via-transparent to-gray-100/30"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`grid lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full mb-8 text-xs tracking-wide">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                GESTION D'INFLUENCE
              </div>

              <h1 id="hero-title" className="text-6xl lg:text-7xl font-light text-gray-900 mb-8 leading-tight tracking-tight">
                Pilotez toutes vos
                <br />
                <span className="font-normal">campagnes d'influence</span>
                <br />
                <span className="text-gray-600">au même endroit</span>
              </h1>

              <div className="flex items-center gap-3 mb-12">
                <div className="w-12 h-0.5 bg-gray-900"></div>
              </div>

              <p className="text-lg text-gray-600 mb-12 leading-relaxed font-light max-w-lg">
                Collaborez avec des créateurs de contenu et gérez vos campagnes en toute simplicité
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <button
                  onClick={goToSignup}
                  className="group px-8 py-4 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all font-light flex items-center justify-center gap-2"
                >
                  Découvrir
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <div className="flex items-center gap-3 px-6 py-4 bg-white rounded-full border border-gray-200">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-white"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-white"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-400 border-2 border-white"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 border-2 border-white"></div>
                  </div>
                  <span className="text-sm text-gray-600 font-light">+3000 créateurs</span>
                </div>
              </div>

              <div className="flex items-center gap-8">
                <div>
                  <div className="text-3xl font-light text-gray-900 mb-1">+1000</div>
                  <div className="text-sm text-gray-500 font-light">Campagnes</div>
                </div>
                <div className="w-px h-12 bg-gray-200"></div>
                <div>
                  <div className="text-3xl font-light text-gray-900 mb-1">50+</div>
                  <div className="text-sm text-gray-500 font-light">Agences</div>
                </div>
              </div>
            </div>

            {/* Right Content - App Screenshots */}
            <div className="relative lg:block hidden">
              <div className="space-y-6">
                {/* Screenshot 1 - Tâches influenceurs */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all hover:-translate-y-1 border border-gray-100">
                  <img
                    src="https://buzndosrenrpxnadomia.supabase.co/storage/v1/object/public/screenshots/calendar-tasks-screenshot.png"
                    alt="Planning mensuel des tâches influenceurs"
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Screenshot 2 - Page Discover */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all hover:-translate-y-1 border border-gray-100">
                  <img
                    src="https://buzndosrenrpxnadomia.supabase.co/storage/v1/object/public/screenshots/landing-screenshot-2.png"
                    alt="Page de découverte des influenceurs"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section aria-labelledby="features-heading" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full mb-8 text-xs tracking-wide">
              FONCTIONNALITÉS
            </div>
            <h2 id="features-heading" className="text-5xl font-light text-gray-900 mb-6 tracking-tight max-w-3xl">
              Tout ce dont vous avez besoin pour gérer vos <span className="font-normal">campagnes d'influence</span>
            </h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-0.5 bg-gray-900"></div>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl font-light leading-relaxed">
              Une plateforme complète qui centralise la gestion de vos collaborations, de la découverte à l'analyse des performances
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Handshake,
                title: "Collaborations",
                description: "Pilotez chaque collab avec timeline, livrables et statut. Partagez un lien en temps réel à la marque."
              },
              {
                icon: BarChart3,
                title: "Dashboard CA",
                description: "CA mensuel, contrats en cours, devis envoyés et top créateurs. Une vision business immédiate."
              },
              {
                icon: Building2,
                title: "CRM Clients",
                description: "Toute l'historique d'un client en un clic : contacts, campagnes, montants, statuts."
              },
              {
                icon: Users,
                title: "Base Influenceurs",
                description: "Recherchez parmi des milliers d'influenceurs avec filtres avancés et statistiques complètes."
              },
              {
                icon: Calendar,
                title: "Tâches Équipe",
                description: "Calendrier hebdo, assignation par membre et suivi d'avancement en temps réel."
              },
              {
                icon: CheckCircle2,
                title: "Tâches Influenceurs",
                description: "Envoyez un lien aux créateurs : ils cochent quand c'est fait. Délais maîtrisés."
              },
              {
                icon: Euro,
                title: "Comptabilité",
                description: "Paiements en attente/versés, pourcentages et montants à reverser par campagne."
              },
              {
                icon: FolderOpen,
                title: "Ressources",
                description: "Centralisez contrats, briefs et devis par catégorie dans un seul espace."
              },
              {
                icon: TrendingUp,
                title: "Market Watch",
                description: "Veille constante sur les collaborations du marché pour rester informé des tendances et opportunités."
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group bg-gray-50 hover:bg-gray-100 rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1"
                aria-label={`Fonctionnalité: ${feature.title}`}
              >
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed font-light text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full mb-8 text-xs tracking-wide">
              À PROPOS
            </div>
            <h2 className="text-4xl font-light text-gray-900 mb-6 tracking-tight">
              Un CRM pensé pour les <span className="font-normal">agences, marques et créateurs</span>
            </h2>
          </div>

          <div className="space-y-6 text-gray-600 leading-relaxed font-light">
            <p className="text-lg">
              Campaly s'impose comme le <span className="font-normal text-gray-900">CRM marketing d'influence</span> de référence pour les agences spécialisées et les marques souhaitant optimiser leurs <span className="font-normal text-gray-900">collaborations d'influence</span>. Notre plateforme révolutionne la gestion des campagnes en centralisant tous vos outils dans un seul logiciel performant et intuitif.
            </p>
            <p className="text-lg">
              Grâce à notre CRM influence avancé, vous bénéficiez d'une vision globale de vos projets : de la prospection d'influenceurs jusqu'au <span className="font-normal text-gray-900">reporting campagnes influence</span> détaillé. Notre solution accompagne plus de 50 agences dans leur croissance.
            </p>
            <p className="text-lg">
              Le module de <span className="font-normal text-gray-900">gestion collaborations influence</span> de Campaly automatise vos processus : création de briefs, suivi des livrables, validation des contenus et facturation. Fini les tableaux Excel et les emails perdus.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-32 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div>
              <div className="text-6xl font-light text-white mb-4">1000+</div>
              <div className="w-12 h-0.5 bg-white/20 mb-4"></div>
              <div className="text-white/60 font-light">Campagnes d'influence gérées avec succès</div>
            </div>
            <div>
              <div className="text-6xl font-light text-white mb-4">+3000</div>
              <div className="w-12 h-0.5 bg-white/20 mb-4"></div>
              <div className="text-white/60 font-light">Créateurs de contenu dans notre réseau</div>
            </div>
            <div>
              <div className="text-6xl font-light text-white mb-4">50+</div>
              <div className="w-12 h-0.5 bg-white/20 mb-4"></div>
              <div className="text-white/60 font-light">Agences partenaires qui nous font confiance</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full mb-8 text-xs tracking-wide">
            COMMENCER
          </div>
          <h2 className="text-5xl lg:text-6xl font-light text-gray-900 mb-8 leading-tight tracking-tight">
            Prêt à transformer votre <span className="font-normal">gestion d'influence</span> ?
          </h2>
          <p className="text-lg text-gray-600 mb-12 font-light leading-relaxed max-w-2xl mx-auto">
            Rejoignez les agences et marques qui optimisent leurs campagnes avec Campaly
          </p>
          <button
            onClick={goToSignup}
            className="group inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all font-light"
          >
            Commencer maintenant
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto py-16 px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
                  alt="Campaly"
                  className="w-9 h-9 object-contain"
                />
                <span className="text-2xl font-light tracking-tight">Campaly</span>
              </div>
              <p className="text-white/60 font-light text-sm leading-relaxed mb-4">
                Plateforme de gestion de campagnes d'influence pour agences et marques
              </p>
              <a
                href="https://www.linkedin.com/company/getcampaiyn/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Linkedin className="w-5 h-5 text-white" />
              </a>
            </div>

            <div>
              <h3 className="text-white font-normal mb-4">Contact</h3>
              <div className="space-y-2 text-white/60 font-light text-sm">
                <p>
                  <a href="mailto:contact@campaly.com" className="hover:text-white transition-colors">
                    contact@campaly.com
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-white font-normal mb-4">Légal</h3>
              <div className="space-y-2 text-white/60 font-light text-sm">
                <p>
                  <a href="/mentions-legales" className="hover:text-white transition-colors">
                    Mentions légales
                  </a>
                </p>
                <p>
                  <a href="/politique-confidentialite" className="hover:text-white transition-colors">
                    Politique de confidentialité
                  </a>
                </p>
                <p>
                  <a href="/cgv" className="hover:text-white transition-colors">
                    Conditions générales de vente
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10">
            <div className="text-white/40 font-light text-sm text-center">
              © 2025 Campaly. Tous droits réservés.
            </div>
          </div>
        </div>
      </footer>
      </>
    </div>
  );
}
